import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L2 from "leaflet";
import "leaflet/dist/leaflet.css";
import { ArrowRight } from "lucide-react";
import { useLang, L } from "@/i18n";
import { api } from "@/api";
import { SECTIONS, TYPE_COLORS, SECTION_BY_TYPE } from "@/config";
import { SectionHead } from "@/components/Widgets";

const dot = (color) =>
  L2.divIcon({
    className: "",
    html: `<div style="width:18px;height:18px;border-radius:50%;background:${color};border:3px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,.35)"></div>`,
    iconSize: [18, 18],
    iconAnchor: [9, 9],
  });

export default function MapPage() {
  const { lang, t } = useLang();
  const [items, setItems] = useState([]);
  const [active, setActive] = useState([]);

  useEffect(() => {
    api.get("/items", { params: { limit: 2000 } })
      .then((r) => setItems(r.data.filter((i) => i.lat && i.lng)))
      .catch(() => {});
  }, []);

  const typesPresent = useMemo(() => [...new Set(items.map((i) => i.type))], [items]);
  const visible = active.length === 0 ? items : items.filter((i) => active.includes(i.type));

  const toggle = (type) =>
    setActive((prev) => (prev.includes(type) ? prev.filter((x) => x !== type) : [...prev, type]));

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 py-12" data-testid="map-page">
      <SectionHead kicker="Follonica Like" title={t("map.title")} sub={t("map.sub")} />

      <div className="flex gap-2 overflow-x-auto pb-3 -mx-1 px-1" data-testid="map-filters">
        {SECTIONS.filter((s) => typesPresent.includes(s.type)).map((s) => {
          const on = active.length === 0 || active.includes(s.type);
          const Icon = s.icon;
          return (
            <button key={s.type} data-testid={`map-filter-${s.type}`} onClick={() => toggle(s.type)}
              className={`rounded-full pl-2.5 pr-4 py-2 text-xs font-bold whitespace-nowrap inline-flex items-center gap-2 transition-all ${on ? "text-white" : "bg-white text-grafite/60 border border-black/10"}`}
              style={on ? { background: s.color } : {}}>
              <Icon className="w-3.5 h-3.5" /> {t(s.navKey)}
            </button>
          );
        })}
      </div>

      <div className="mt-4 rounded-3xl overflow-hidden shadow-[0_16px_50px_rgba(14,110,140,0.15)] h-[62vh] min-h-[420px]" data-testid="map-container">
        <MapContainer center={[42.93, 11.0]} zoom={11} scrollWheelZoom className="h-full w-full">
          <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="© OpenStreetMap contributors" />
          {visible.map((i) => (
            <Marker key={i.slug} position={[i.lat, i.lng]} icon={dot(TYPE_COLORS[i.type] || "#0E6E8C")}>
              <Popup>
                <div className="min-w-[180px]">
                  <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color: TYPE_COLORS[i.type] }}>
                    {SECTION_BY_TYPE[i.type] ? t(SECTION_BY_TYPE[i.type].navKey) : i.type}
                  </p>
                  <p className="font-bold text-sm mt-0.5">{L(i.title, lang)}</p>
                  <Link to={`/scheda/${i.slug}`} data-testid={`map-popup-${i.slug}`}
                    className="mt-1.5 inline-flex items-center gap-1 text-xs font-bold text-[#E8703A]">
                    {t("common.discover")} <ArrowRight className="w-3 h-3" />
                  </Link>
                </div>
              </Popup>
            </Marker>
          ))}
        </MapContainer>
      </div>
    </div>
  );
}
